import * as React from "react";

import { VerticalButtonPanel } from "../subs/ButtonPanel";
import { PageSection } from "../subs/PageSection";
import { WorkshopInfoPanel } from "../subs/WorkshopInfoPanel";

import ListIcon from '@mui/icons-material/List';
import { httpGet } from "../Web";
import AddBoxIcon from '@mui/icons-material/AddBox';
import CreateIcon from '@mui/icons-material/Create';
import ImportExportIcon from '@mui/icons-material/ImportExport';

import { Page } from "../subs/Page";
import { formatDateString } from "../Web";
import { ButtonLink } from "../subs/ButtonLink";
import { Workshop, WorkshopStatus, getWorkshops } from "../types/Workshop";
import { getUserWorkshops, isGlobalAdmin } from "../types/User";
import { UserContext } from "../UserProvider";
import { Alert, Button, Stack, ToggleButton, ToggleButtonGroup, Typography} from "@mui/material";
import { WorkshopUser } from "../types/WorkshopUser";
import { StandaloneInfo } from "../subs/StandaloneInfo";
import { AddProductToWorkshopLocalModal } from '../modals/AddProductToWorkshopModal';
import { ImportMultiProductModal } from '../modals/ImportMultiProductModal';

const ImportPanel = ({ workshopId }:
    { workshopId: number }) => {

    //  const location = useLocation();
    const [importModalOpenState, setImportModalOpenState] = React.useState<boolean>(false);

    return <React.Fragment>
        <ImportMultiProductModal workshopId={workshopId} openState={importModalOpenState} setOpenState={setImportModalOpenState} />
        <Button fullWidth startIcon={<ImportExportIcon />} onClick={() => { setImportModalOpenState(true) }} >Import from previous Workshops</Button>
    </React.Fragment>
}

function WorkshopPageSection({ ws, isWSUser }: { ws: Workshop, isWSUser: boolean }) {

    const [addModalOpenState, setAddModalOpenState] = React.useState<boolean>(false);

    if(ws.status === WorkshopStatus.Created)
        return <></>

    const changeProductsBtn = <ButtonLink disabled={!isWSUser} to={`/ViewUserProducts/${ws.id}`} fullWidth startIcon={<CreateIcon />}>{"Change products"}</ButtonLink>
    //ViewUserProducts
    const viewUsers = <ButtonLink to={`/WSUsers/${ws.id}`} disabled={ws.status === WorkshopStatus.Closed} fullWidth startIcon={<ListIcon />}>{"View users"}</ButtonLink>
    const addProductBtn = <Button disabled={!isWSUser} fullWidth={true} startIcon={<AddBoxIcon />} onClick={() => { setAddModalOpenState(true); }}>{'Add Product'}</Button>
    const viewChangeBtn = <ButtonLink to={`/ArrivalInformation/${ws.id}`} disabled={(!isWSUser || ws.status === WorkshopStatus.Closed)} fullWidth startIcon={<CreateIcon />}>{"Hotel / Arrival Information"}</ButtonLink>
    const importProductsBtn = <ImportPanel workshopId={ws.id} />

    const usersProducts = [addProductBtn, changeProductsBtn, importProductsBtn];
    const arrival = [viewChangeBtn, viewUsers];

    return <>
        <AddProductToWorkshopLocalModal workshopID={ws.id} workshopProducts={null} setWorkshopProducts={null}
            openState={addModalOpenState} setOpenState={setAddModalOpenState} workshopProductsInterfaces={null} setWorkshopProductInterfaces={null} />

        <PageSection heading={ws.name}>
            <WorkshopInfoPanel location={ws.location} startDate={formatDateString(ws.startDate) as string} endDate={formatDateString(ws.endDate) as string} supportMail={ws.contactEmail} logInfo={ws.informationUrl} />
            <VerticalButtonPanel heading={"Products"}>{[usersProducts]}</VerticalButtonPanel>
            <VerticalButtonPanel heading={"Attendees"}>{arrival}</VerticalButtonPanel>
        </PageSection>
    </>
}

const userWorkshopFilterPredicate = (ws: Workshop, uws: WorkshopUser[], showAll: boolean) => {
    return showAll ? (ws.status === WorkshopStatus.PreRegistration || ws.status === WorkshopStatus.Registration || ws.status === WorkshopStatus.Testing) :
        typeof uws.find(uw => uw.workshopId === ws.id) !== 'undefined'
        && (ws.status === WorkshopStatus.PreRegistration || ws.status === WorkshopStatus.Registration
            || ws.status === WorkshopStatus.Testing || ws.status === WorkshopStatus.Invisible);
}

export const Workshops = () => {
    const userContext = React.useContext(UserContext);
    const [workshops, setWorkshops] = React.useState<Workshop[]>([]);
    const [userWorkshops, setUserWorkshops] = React.useState<WorkshopUser[]>([]);
    const [workshopError, setWorkshopError] = React.useState<string | null>(null);
    const [userIsGlobalAdmin, setUserIsGlobalAdmin] = React.useState<boolean>(false);
    const [selectedDisplayOption, setSelectedDisplayOption] = React.useState<"myWorkshops" | "allWorkshops">("myWorkshops");

    // after 60 secs of idle (or not recording new results) refresh the results
    React.useEffect(() => {
        console.log(`initializing user account check`);
        const interval = setInterval(() => {
            httpGet('/api/account/checkloginstatus', new AbortController());
        }, 60000);

        return () => {
            console.log(`clearing user account check Timer`);
            clearInterval(interval);
        };
    }, []);

    React.useEffect(()=>{
        const fetch = async() => {
            const sharedAbortController = new AbortController();

            try {
                const userWorkshops = await getUserWorkshops(userContext.user?.id!!, sharedAbortController)
                const workshops = await getWorkshops(sharedAbortController);
                const globalAdminFlag = await isGlobalAdmin(userContext.user.id!!, new AbortController());

                if(workshops.failed) {
                    setWorkshopError(workshops.errorText);
                    return;
                }

                if(userWorkshops.failed) {
                    setWorkshopError(workshops.errorText);
                    return;
                }

                setUserIsGlobalAdmin(globalAdminFlag);
                setWorkshops(workshops.result);
                setUserWorkshops(userWorkshops.result);
            } catch(error) {
                setWorkshopError(String(error));
                console.log(error);
            }
        }
        
        fetch();
    }, [userContext]);
    
    let workshopSections = workshops.filter(ws => userWorkshopFilterPredicate(ws, userWorkshops, selectedDisplayOption === "allWorkshops"))
        .map(ws => <WorkshopPageSection ws={ws} isWSUser={userIsGlobalAdmin || (userWorkshops.find(uws => uws.workshopId === ws.id) !== undefined)} />);

    const pageHeading = <>
        <Stack direction="row" justifyContent={'space-between'}>
            <Typography variant="h4">{"Workshops"}</Typography>

            <ToggleButtonGroup
                exclusive
                value={selectedDisplayOption}
                onChange={(_event, nextView) => { setSelectedDisplayOption(nextView); }}
            >
                <ToggleButton value="myWorkshops">{"My Workshops"}</ToggleButton>
                <ToggleButton value="allWorkshops">{"All Workshops"}</ToggleButton>
            </ToggleButtonGroup>
        </Stack>
    </>

    return <>
        <Page heading={pageHeading} errorComponent={workshopError ? <Alert severity='error'>{workshopError}</Alert> : null}>
            {[(workshopSections.length === 0 && selectedDisplayOption === "myWorkshops") ? <StandaloneInfo severity="info" text={"There are no upcoming Workshops that you have registered for."}/> :
                ((workshopSections.length === 0 && selectedDisplayOption === "allWorkshops") ? <StandaloneInfo severity="info" text={"There are no upcoming workshops."} /> : workshopSections)]}
        </Page>
    </>
}